import type { PlatformsWidgetModel } from '.';
import { makeHeaderElements } from '../WidgetModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { PlatformsWidgetState } from './PlatformsSelfOwnedModel';
import { PlatformsSelfOwnedModel, platformsWidgetStateSchema } from './PlatformsSelfOwnedModel';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND } from 'src/features/aggregated-risk/config';
import type { AggregatedRiskWidgetConfig } from 'src/features/aggregated-risk/config';
import { inject, injectable } from 'src/features/ioc';
import type { AggregatedRiskWidgetRuntimeState } from 'src/models/AggregatedRiskWidgetModel';
import { AggregatedRiskWidgetTableModel } from 'src/models/AggregatedRiskWidgetModel/AggregatedRiskWidgetTableModel';
import type { TableModel } from 'src/models/TableModel';
import { getAggregatedRiskTableQueryOptions } from 'src/queries/aggregatedRiskTables';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import { decodeSelfOwnedWidgetState } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { GetInitialData, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';
import type { ReactBindings } from 'src/types/bindings';

export function createPlatformsWidgetHeaderElements(config: AggregatedRiskWidgetConfig): WidgetHeaderElement[] {
    return makeHeaderElements<PlatformsWidgetModel['eventBus']>([
        ...COMMON_HEADER_ELEMENTS,
        { type: 'stickyFilter', position: 40, priority: 80 },
        { type: 'platforms', position: 65, priority: 70 },
        {
            type: 'columnTemplateMenu',
            position: 70,
            priority: 20,
            columnTemplateKey: config.templateKey,
            alwaysUseGridSettings: true,
        },
        { type: 'tableRowCount', position: 75, priority: 11 },
        { type: 'connectionStatus', position: 76, priority: 12, overflowPosition: 5 },
    ]);
}

@injectable()
export class PlatformsWidgetModelImpl implements PlatformsWidgetModel {
    static readonly stateSchema = platformsWidgetStateSchema;

    static defaultState(): PlatformsWidgetState {
        return { columnTemplate: null, filter: null, platforms: null };
    }

    static fromJSON(container: ReactWidgetContainer): PlatformsWidgetModelImpl {
        const getInitialData = container.get('GetInitialData') as GetInitialData<unknown>;
        const raw = getInitialData();
        const validated = decodeSelfOwnedWidgetState(PlatformsWidgetModelImpl.stateSchema, raw);
        container.bind('InitialWidgetState').toConstantValue(validated);
        return container.get('PlatformsWidgetModel') as PlatformsWidgetModelImpl;
    }

    readonly #model: PlatformsSelfOwnedModel;
    readonly #table: TableModel;
    readonly #tableModel: AggregatedRiskWidgetTableModel;
    readonly #disposableStack = new DisposableStack();
    readonly headerElements: WidgetHeaderElement[];

    constructor(
        @inject('WidgetId') tabId: WidgetId,
        @inject('WidgetContainerController') container: WidgetContainerController,
        @inject('InitialWidgetState') initialState: PlatformsWidgetState,
        @inject('Logger') logger: Logger,
        @inject('SlugMapService') slugMapService: ReactBindings['SlugMapService'],
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('TableModel') table: TableModel,
    ) {
        const persisted = initialState;
        const config = AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND.platforms;

        this.#table = this.#disposableStack.use(table);

        this.#model = this.#disposableStack.use(
            new PlatformsSelfOwnedModel({
                container,
                tabId,
                persisted,
                logger,
                queryClient,
                slugMapService,
                table: this.#table,
            }),
        );

        this.#tableModel = this.#disposableStack.use(
            new AggregatedRiskWidgetTableModel({
                columnTemplate: this.#model.columnTemplate,
                columnTemplateError: this.#model.columnTemplateError,
                config,
                devtoolsName: 'Platforms widget',
                groupByIndicatorIds: this.#model.groupByIndicatorIds,
                filters: this.#model.tableFilters,
                slugMapServiceProvider,
                table: this.#table,
                tableKind: 'platforms',
                tableQueryOptionsFactory: getAggregatedRiskTableQueryOptions,
                widgetId: tabId,
            }),
        );

        this.headerElements = createPlatformsWidgetHeaderElements(config);
    }

    get type(): 'platforms' {
        return 'platforms';
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model.dirty;
    }

    get state(): ReadonlySignal<AggregatedRiskWidgetRuntimeState> {
        return this.#tableModel.state;
    }

    get columnTemplate(): ReadonlySignal<ColumnTemplate | null> {
        return this.#model.columnTemplate;
    }

    get groupByIndicatorIds(): ReadonlySignal<readonly string[]> {
        return this.#model.groupByIndicatorIds;
    }

    get columnTemplateError(): ReadonlySignal<Error | null> {
        return this.#model.columnTemplateError;
    }

    get tableFilters(): PlatformsWidgetModel['tableFilters'] {
        return this.#model.tableFilters;
    }

    get selectedPlatforms(): ReadonlySignal<string[]> {
        return this.#model.selectedPlatforms;
    }

    get eventBus(): PlatformsWidgetModel['eventBus'] {
        return this.#model.eventBus;
    }

    get table(): TableModel {
        return this.#table;
    }

    toJSON(): PlatformsWidgetState {
        return this.#model.toJSON();
    }

    markClean(): void {
        this.#model.markClean();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
